<template>
  <div class="space-y-6">
    <!-- Loading state -->
    <div
      v-if="store.isGenerating"
      class="relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-b from-surface to-zinc-50/80 px-8 py-16 text-center dark:border-white/10 dark:from-surface-dark dark:to-base-dark/80"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        class="pointer-events-none absolute inset-0 opacity-40 dark:opacity-25"
        aria-hidden="true"
      >
        <div
          class="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(139,92,246,0.35),transparent_55%)]"
        />
      </div>
      <div class="relative mx-auto max-w-sm">
        <div class="relative mx-auto flex h-16 w-16 items-center justify-center">
          <div
            class="absolute h-16 w-16 animate-spin rounded-full border-[3px] border-violet-500/20 border-t-violet-600 dark:border-violet-500/15 dark:border-t-violet-400"
          />
          <svg
            class="relative h-7 w-7 text-violet-600 dark:text-violet-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
            />
          </svg>
        </div>
        <h3 class="mt-8 text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Generating your story
        </h3>
        <p class="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          Gemini is writing — this often takes 15–45 seconds.
        </p>
        <div class="mt-6 flex justify-center gap-1.5" aria-hidden="true">
          <span
            class="h-2 w-2 rounded-full bg-violet-500 opacity-70 animate-bounce dark:bg-violet-400"
          />
          <span
            class="h-2 w-2 rounded-full bg-violet-500 opacity-70 animate-bounce dark:bg-violet-400 [animation-delay:120ms]"
          />
          <span
            class="h-2 w-2 rounded-full bg-violet-500 opacity-70 animate-bounce dark:bg-violet-400 [animation-delay:240ms]"
          />
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div
      v-else-if="store.error"
      class="rounded-2xl border border-red-500/20 bg-red-500/5 p-6 text-center"
    >
      <p class="text-sm text-red-500 dark:text-red-400">{{ store.error }}</p>
      <button
        @click="store.generateStory()"
        class="mt-4 rounded-xl border border-red-500/30 px-4 py-2 text-sm font-medium text-red-500 transition hover:border-red-500/50 dark:text-red-300"
      >
        Try Again
      </button>
    </div>

    <!-- Story result -->
    <template v-else>
      <div
        class="rounded-2xl border border-zinc-200 bg-surface p-6 dark:border-white/5 dark:bg-surface-dark"
        :class="store.selectedLanguage === 'arabic' ? 'text-right' : 'text-left'"
        :dir="store.selectedLanguage === 'arabic' ? 'rtl' : 'ltr'"
      >
        <p class="whitespace-pre-wrap text-sm leading-relaxed text-zinc-700 dark:text-zinc-200">
          {{ store.generatedStory }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex flex-wrap items-center gap-3">
        <!-- TTS -->
        <button
          @click="toggleSpeech"
          class="inline-flex items-center gap-2 rounded-xl border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-500 transition hover:border-zinc-300 hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:text-zinc-50"
        >
          <svg v-if="!isPlaying" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"/>
          </svg>
          <svg v-else class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5"/>
          </svg>
          {{ isPlaying ? 'Pause' : 'Listen' }}
        </button>

        <!-- Save -->
        <button
          @click="showSaveDialog = true"
          class="inline-flex items-center gap-2 rounded-xl border border-violet-500/30 bg-violet-500/10 px-4 py-2 text-sm font-semibold text-violet-700 transition hover:border-violet-500/50 dark:text-violet-300"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"/>
          </svg>
          Save Story
        </button>
      </div>

      <!-- Save dialog -->
      <div
        v-if="showSaveDialog"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm dark:bg-black/60"
        @click.self="showSaveDialog = false"
      >
        <div class="w-full max-w-md rounded-2xl border border-zinc-200 bg-base p-6 shadow-2xl dark:border-white/10 dark:bg-base-dark">
          <h3 class="text-lg font-semibold text-zinc-900 dark:text-zinc-50">Save Your Story</h3>
          <input
            v-model="storyTitle"
            type="text"
            maxlength="50"
            placeholder="e.g. Red Riding Hood"
            class="mt-4 w-full rounded-xl border border-zinc-200 bg-surface px-4 py-2.5 text-sm text-zinc-800 placeholder-zinc-400 outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 dark:border-white/10 dark:bg-surface-dark dark:text-zinc-100 dark:placeholder-zinc-600"
            @keyup.enter="handleSave"
          />
          <div class="mt-4 flex justify-end gap-3">
            <button
              @click="showSaveDialog = false"
              class="rounded-xl border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-500 transition hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:text-zinc-50"
            >
              Cancel
            </button>
            <button
              @click="handleSave"
              :disabled="!storyTitle.trim() || isSaving"
              class="rounded-xl bg-violet-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:opacity-50"
            >
              {{ isSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
          <p v-if="saveSuccess" class="mt-3 text-center text-sm text-emerald-500 dark:text-emerald-400">
            Story saved successfully!
          </p>
        </div>
      </div>
    </template>

    <!-- Navigation -->
    <div class="flex items-center justify-between border-t border-zinc-200 pt-6 dark:border-white/5">
      <button
        @click="$emit('back')"
        class="rounded-xl border border-zinc-200 px-5 py-2 text-sm font-medium text-zinc-500 transition hover:border-zinc-300 hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:text-zinc-50"
      >
        &larr; Edit Prompt
      </button>
      <button
        @click="$emit('new-story')"
        class="rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:shadow-violet-500/40"
      >
        New Story
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useStoryStore } from '@/stores/story';

defineEmits(['back', 'new-story']);

const store = useStoryStore();

const showSaveDialog = ref(false);
const storyTitle = ref('');
const isSaving = ref(false);
const saveSuccess = ref(false);

const isPlaying = ref(false);
let utterance = null;

function toggleSpeech() {
  if (!utterance) {
    utterance = new SpeechSynthesisUtterance(store.generatedStory);
    utterance.lang = store.selectedLanguage === 'english' ? 'en-US' : 'ar-EG';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.onend = () => {
      isPlaying.value = false;
      utterance = null;
    };
    speechSynthesis.cancel();
    speechSynthesis.speak(utterance);
    isPlaying.value = true;
  } else if (speechSynthesis.paused) {
    speechSynthesis.resume();
    isPlaying.value = true;
  } else {
    speechSynthesis.pause();
    isPlaying.value = false;
  }
}

async function handleSave() {
  if (!storyTitle.value.trim()) return;
  isSaving.value = true;
  try {
    await store.saveStory(storyTitle.value.trim());
    saveSuccess.value = true;
    setTimeout(() => {
      showSaveDialog.value = false;
      saveSuccess.value = false;
      storyTitle.value = '';
    }, 1500);
  } catch (e) {
    console.error('Save failed:', e);
  } finally {
    isSaving.value = false;
  }
}

onBeforeUnmount(() => {
  speechSynthesis.cancel();
  utterance = null;
});
</script>
